import { LiveSample } from '@/components/tools/LiveSample';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight } from 'lucide-react';
import { useToolCatalog, type CatalogCategory } from '@/lib/tool-catalog';
import { trackEvent } from '@/lib/analytics';
import { cn } from '@/lib/utils';

// EVERY TOOL, BY WHAT IT IS FOR (owner 2026-10-16): music videos next to the
// Spotify loop and the hook pack, story videos next to faceless videos and
// captions, storybooks next to the vows print. One card style for all of
// them: a real output, the name, one line, the price, one button.

const TILE =
  'hero-hairline hero-tile relative flex h-full flex-col overflow-hidden rounded-[1.35rem] bg-[linear-gradient(180deg,rgba(255,255,255,0.06),rgba(255,255,255,0.018))] shadow-[0_24px_48px_-28px_rgba(0,0,0,0.8)]';

export function ToolCatalogGrid({ only, headings = true, className }: { only?: readonly CatalogCategory[]; headings?: boolean; className?: string }) {
  const { t } = useTranslation();
  const catalog = useToolCatalog().filter((c) => !only || only.includes(c.key));
  return (
    <div className={cn('space-y-10', className)} data-testid="tool-catalog">
      {catalog.map((cat) => (
        <section key={cat.key} aria-labelledby={`cat-${cat.key}`} data-testid={`tool-cat-${cat.key}`}>
          {headings && (
            <h2 id={`cat-${cat.key}`} className="mb-4 font-display text-[1.35rem] font-bold leading-tight sm:text-[1.6rem]">
              {t(`toolCats.${cat.key}`)}
            </h2>
          )}
          <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {cat.tools.map((tool) => (
              <li key={tool.id}>
                <Link
                  to={tool.path}
                  data-testid={`catalog-${tool.studio ?? tool.id}`}
                  onClick={() => trackEvent('tool_catalog_clicked', { tool: tool.id })}
                  className={cn(TILE, 'group transition-transform duration-500 [@media(hover:hover)_and_(pointer:fine)]:hover:-translate-y-1')}
                >
                  <span className="relative block aspect-[16/9] overflow-hidden rounded-t-[1.35rem] bg-black">
                    <LiveSample poster={tool.poster} src={tool.clip} gallery={tool.gallery} />
                    <span
                      className={cn(
                        'absolute right-3 top-3 rounded-full bg-black/60 px-3 py-1 text-[13px] font-bold tabular-nums ring-1 ring-inset backdrop-blur-sm',
                        tool.free ? 'text-accent ring-accent/35' : 'text-primary ring-primary/35',
                      )}
                    >
                      {tool.price}
                    </span>
                  </span>
                  <span className="flex flex-1 flex-col p-5">
                    <span className="font-display text-[1.15rem] font-semibold leading-snug text-foreground">{tool.name}</span>
                    <span className="mt-2 flex-1 text-pretty text-[15px] leading-relaxed text-white/60">{tool.line}</span>
                    <span className="mt-4 inline-flex min-h-[44px] items-center gap-1.5 self-start rounded-full border border-white/15 bg-white/[0.04] px-4 text-[15px] font-semibold text-foreground group-hover:border-primary/50">
                      {tool.quick ? t(`qt.tools.${tool.quick}.open`) : t('toolCats.open')}
                      <ArrowUpRight className="h-4 w-4 text-primary" aria-hidden />
                    </span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
